import { useContext, useState } from 'react';
import { CardsContext } from '../context/CardsContext';
import { SiteContentContext } from '../context/SiteContentContext';
import { sendConfirmEmail } from '../utils/email';
import { getBookingTemplate } from '../utils/emailTemplates';
import useIdleLogout from '../hooks/useIdleLogout';
import BigCalendar from './BigCalendar';
import EmailSettings from './EmailSettings';
import EmailPreviewModal from './EmailPreviewModal';
import TotpSetup from './TotpSetup';

const MONTHS_GEN = ['января','февраля','марта','апреля','мая','июня',
  'июля','августа','сентября','октября','ноября','декабря'];
const TYPE_LABEL = { study: 'Обучение', adventures: 'Путешествия', races: 'Регаты' };

const fmtSlot = (slot) => {
  if (!slot.isoDate) return String(slot);
  const [y, m, d] = slot.isoDate.split('-').map(Number);
  return `${d} ${MONTHS_GEN[m - 1]} ${y}`;
};

const ROLE_NAME = { admin: 'Сергей', admin2: 'Админ 2', manager: 'Юлия', manager2: 'Менеджер 2' };
const ADDED_BY_STYLE = {
  'Сергей':     { bg: '#e3f0ff', color: '#1565c0', border: '#90caf9' },
  'Админ 2':    { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
  'Юлия':       { bg: '#fce4ec', color: '#c62828', border: '#f48fb1' },
  'Менеджер 2': { bg: '#fff3e0', color: '#e65100', border: '#ffcc80' },
};

const SlotCard = ({ card, type, updateCardLive, content }) => {
  const [inputs, setInputs]       = useState({}); // { [idx]: { name, email } }
  const [emailPending, setEmailPending] = useState(null);
  const slots = card.availableDates || [];
  const role = sessionStorage.getItem('auth_role') || 'admin';
  const addedBy = content?.staffNames?.[role] || ROLE_NAME[role] || role;

  const updateSlots = (newSlots) => updateCardLive(type, card.id, { availableDates: newSlots });

  const setField = (idx, field, val) =>
    setInputs(prev => ({ ...prev, [idx]: { ...(prev[idx] || {}), [field]: val } }));

  const addToSlot = (idx) => {
    const firstName = (inputs[idx]?.firstName || '').trim();
    const lastName  = (inputs[idx]?.lastName  || '').trim();
    const email     = (inputs[idx]?.email     || '').trim();
    if (!firstName) return;
    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    const slot = slots[idx];
    const newSlots = slots.map((s, i) => {
      if (i !== idx) return s;
      const entry = { name: fullName, email: email || undefined, addedBy };
      return { ...s, participants: [...(s.participants || []), entry], spaces: Math.max(0, (s.spaces ?? 0) - 1) };
    });
    updateSlots(newSlots);
    setInputs(prev => ({ ...prev, [idx]: { firstName: '', lastName: '', email: '' } }));

    if (email) {
      const MG = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];
      const slotDate = slot?.isoDate
        ? (() => { const [y,m,d] = slot.isoDate.split('-').map(Number); return `${d} ${MG[m-1]} ${y}`; })()
        : '';
      setEmailPending({
        to:           email,
        name:         fullName,
        firstName,
        event:        card.title    || '',
        date:         slotDate,
        dateEnd:      card.endDate  ? (() => { const [y,m,d] = card.endDate.split('-').map(Number); return `${d} ${MG[m-1]} ${y}`; })() : '',
        location:     card.location || '',
        ...getBookingTemplate(content, type),
        emailEnabled: content?.confirmEmailEnabled !== false,
      });
    }
  };

  const removeFromSlot = (slotIdx, partIdx) => {
    const newSlots = slots.map((s, i) => {
      if (i !== slotIdx) return s;
      return { ...s, participants: (s.participants || []).filter((_, pi) => pi !== partIdx), spaces: (s.spaces ?? 0) + 1 };
    });
    updateSlots(newSlots);
  };

  return (
    <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: 20, marginBottom: 16 }}>
      <EmailPreviewModal
        pending={emailPending}
        onConfirm={(p) =>
          sendConfirmEmail({ to: p.to, subject: p.subject, body: p.body, attachments: p.attachments, vars: { name: p.name, firstName: p.firstName || p.name.trim().split(/\s+/)[0], event: p.event, date: p.date, dateEnd: p.dateEnd, location: p.location } })
        }
        onClose={() => setEmailPending(null)}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        {card.image && (
          <img src={card.image} alt="" style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
        )}
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{card.title}</div>
          <div style={{ fontSize: 12, color: '#aaa' }}>{TYPE_LABEL[type]}{card.location ? ` · ${card.location}` : ''}</div>
        </div>
      </div>

      {slots.length === 0 && (
        <p style={{ color: '#aaa', fontSize: 13 }}>Нет дат</p>
      )}

      {slots.map((slot, idx) => {
        const participants = slot.participants || [];
        const total = participants.length + (slot.spaces ?? 0);
        const full = slot.spaces <= 0;
        return (
          <div key={idx} style={{ marginBottom: 10, background: '#fafafa', borderRadius: 8, padding: '10px 12px', border: `1px solid ${full ? '#ffd4d4' : '#e8e8e8'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>📅 {fmtSlot(slot)}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: full ? '#e53935' : '#4caf50' }}>
                {participants.length} / {total} мест занято
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
              {participants.map((p, pi) => {
                const pName    = typeof p === 'object' ? p.name    : p;
                const pAddedBy = typeof p === 'object' ? p.addedBy : null;
                const s = ADDED_BY_STYLE[pAddedBy] || {};
                return (
                  <span key={pi} style={{ background: s.bg || '#fdf5f0', border: `1px solid ${s.border || '#f5c9a0'}`, borderRadius: 4, padding: '3px 10px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span>{pName}</span>
                    {pAddedBy && (
                      <span title={`Записал: ${pAddedBy}`} style={{ fontSize: 10, color: s.color, fontWeight: 700, background: '#fff', borderRadius: 3, padding: '0 4px', border: `1px solid ${s.border}` }}>
                        {pAddedBy[0]}
                      </span>
                    )}
                    <button onClick={() => removeFromSlot(idx, pi)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', padding: 0, fontSize: 14, lineHeight: 1 }}>✕</button>
                  </span>
                );
              })}
              {participants.length === 0 && <span style={{ color: '#ccc', fontSize: 13 }}>нет участников</span>}
            </div>
            {!full ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input
                    value={inputs[idx]?.firstName || ''}
                    onChange={e => setField(idx, 'firstName', e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addToSlot(idx))}
                    placeholder="Имя *"
                    style={{ flex: 1, minWidth: 90, padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}
                  />
                  <input
                    value={inputs[idx]?.lastName || ''}
                    onChange={e => setField(idx, 'lastName', e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addToSlot(idx))}
                    placeholder="Фамилия"
                    style={{ flex: 1, minWidth: 90, padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}
                  />
                  <input
                    type="email"
                    value={inputs[idx]?.email || ''}
                    onChange={e => setField(idx, 'email', e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addToSlot(idx))}
                    placeholder="Email"
                    style={{ flex: 1, minWidth: 130, padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}
                  />
                  <button onClick={() => addToSlot(idx)} style={{ background: '#db782e', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    + Добавить
                  </button>
                </div>
                {inputs[idx]?.email && (
                  <div style={{ fontSize: 11, color: '#4caf50', paddingLeft: 2 }}>
                    ✉️ При добавлении отправим письмо-подтверждение на {inputs[idx].email}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: '#e53935', fontWeight: 600 }}>Мест нет</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ManagerPanel = ({ onLogout }) => {
  const { cards, updateCardLive } = useContext(CardsContext);
  const { content } = useContext(SiteContentContext);
  const [tab, setTab] = useState('study');
  const [showEmail, setShowEmail] = useState(false);
  const idleCountdown = useIdleLogout();

  const types = ['study', 'adventures', 'races'];
  const currentCards = (cards[tab] || []).filter(c => c.isPublished && c.availableDates?.length > 0);

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {idleCountdown !== null && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, background: '#e53935', color: '#fff', borderRadius: 10, padding: '12px 20px', fontWeight: 600, fontSize: 15, boxShadow: '0 4px 16px rgba(0,0,0,0.25)' }}>
          ⏱ Автовыход через {idleCountdown} сек. Пошевелите мышью чтобы остаться.
        </div>
      )}

      {/* Шапка */}
      <div style={{ background: '#1a2a3a', color: '#fff', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>👥</span>
          <span style={{ fontWeight: 700, fontSize: 16 }}>Управление записями</span>
        </div>
        <button onClick={onLogout} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>
          Выйти
        </button>
      </div>

      {/* Табы */}
      <div style={{ background: '#fff', borderBottom: '1px solid #eee', display: 'flex', padding: '0 24px', gap: 0 }}>
        {types.map(t => (
          <button key={t} onClick={() => { setTab(t); setShowEmail(false); }} style={{
            padding: '12px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: !showEmail && tab === t ? 700 : 400,
            color: !showEmail && tab === t ? '#db782e' : '#666',
            borderBottom: !showEmail && tab === t ? '2px solid #db782e' : '2px solid transparent',
            fontSize: 14,
          }}>
            {TYPE_LABEL[t]}
          </button>
        ))}
        <button onClick={() => setShowEmail(true)} style={{
          padding: '12px 20px', border: 'none', background: 'none', cursor: 'pointer',
          fontWeight: showEmail ? 700 : 400,
          color: showEmail ? '#db782e' : '#666',
          borderBottom: showEmail ? '2px solid #db782e' : '2px solid transparent',
          fontSize: 14, marginLeft: 'auto',
        }}>
          📧 Email
        </button>
      </div>

      {/* Email-настройки + 2FA */}
      {showEmail && (
        <>
          <EmailSettings />
          <div style={{ maxWidth: 800, margin: '24px auto', padding: '0 24px' }}>
            <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
              <TotpSetup />
            </div>
          </div>
        </>
      )}

      {/* Карточки */}
      {!showEmail && (
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
          {currentCards.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#aaa', marginTop: 60, fontSize: 15 }}>
              Нет опубликованных мероприятий с датами
            </div>
          ) : (
            currentCards.map(card => (
              <SlotCard key={card.id} card={card} type={tab} updateCardLive={updateCardLive} content={content} />
            ))
          )}

          {/* Календарь — внизу */}
          <BigCalendar cards={cards} />
        </div>
      )}
    </div>
  );
};

export default ManagerPanel;
